<template>
  <div class="sql-view-widget-preview cdb-builder-v1" ref="previewWrapper" :class="{ 'svp-stage-mode': isKpiStage }">
    <!-- Loading state -->
    <div v-if="loading" class="svp-loading">
      <div v-if="isKpiStage" class="svp-kpi-stage">
        <div class="svp-kpi-tile">
          <dashboard-widget-skeleton
            :widget-type="messageType"
            :builder-header="true" />
        </div>
      </div>
      <dashboard-widget-skeleton
        v-else
        :widget-type="messageType"
        :show-header="!isTabularMessageType"
        :builder-header="true"
        :show-table-toolbar="false" />
    </div>

    <!-- Error state: a failed preview request is a distinct state from a legitimately
         empty result, so it never renders the "No data returned" message. Flat and
         token-styled (no side-stripe); the summary is category-only so a SQL-view
         failure can't leak schema or query detail, and Retry re-runs the fetch. -->
    <!-- AVA-602: register not configured. Same centred anatomy as the error state below but
         neutral (no warning hue) and no Retry: an administrator has to set the mode first. -->
    <div v-else-if="previewConfigRequired" class="svp-error svp-config-required" role="status">
      <v-icon size="40" class="svp-config-required__icon">mdi-table-cog</v-icon>
      <div class="svp-error__title">{{ notConfiguredCopy.title }}</div>
      <div class="svp-error__detail">{{ notConfiguredCopy.message }}</div>
    </div>

    <div v-else-if="previewError" class="svp-error">
      <v-icon size="40" color="warning">mdi-alert-circle-outline</v-icon>
      <div class="svp-error__title">Preview could not be loaded</div>
      <div class="svp-error__detail">{{ previewError }}</div>
      <v-btn small outlined class="svp-error__retry" @click="refresh">
        <v-icon size="16" left>mdi-refresh</v-icon>
        Retry
      </v-btn>
    </div>

    <!-- No data state -->
    <div v-else-if="!previewWidget || !hasContent" class="d-flex flex-column justify-center align-center" style="height: 100%; opacity: 0.5">
      <v-icon size="48" color="grey">mdi-chart-box-outline</v-icon>
      <span class="text-caption text--disabled mt-2">{{ noDataMessage }}</span>
    </div>

    <!-- Wide raw-data sample: typed headers, formatted cells, row inspector on click -->
    <div v-else-if="isRawTable" class="svp-sample">
      <div class="svp-sample__scroll">
        <table class="svp-sample__table">
          <thead>
            <tr>
              <th
                v-for="col in sampleColumns"
                :key="`h-${col.key}`"
                :class="`svp-t-${col.ftype.key}`"
                :title="col.label">
                <div class="svp-h-name">{{ col.label }}</div>
                <div class="svp-h-type">{{ col.ftype.label }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(cells, rIdx) in formattedRows"
              :key="rIdx"
              class="svp-row"
              :class="{ 'svp-row--sel': inspectorIndex === rIdx }"
              tabindex="0"
              @click="toggleInspector(rIdx)"
              @keydown.enter.prevent="toggleInspector(rIdx)">
              <td
                v-for="(cell, cIdx) in cells"
                :key="`c-${rIdx}-${cIdx}`"
                :class="`svp-t-${sampleColumns[cIdx].ftype.key}`">
                <span
                  class="svp-cell"
                  :class="{ 'svp-cell--mono': cell.mono, 'svp-cell--blank': cell.blank }"
                  :title="cell.title">{{ cell.text }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Row inspector: the whole clicked record, full values, no truncation -->
      <aside class="svp-inspector" :class="{ 'svp-inspector--open': inspectorRow }" aria-label="Row details">
        <template v-if="inspectorRow">
          <div class="svp-inspector__head">
            <span class="svp-inspector__title">Row {{ inspectorIndex + 1 }} of {{ sampleRows.length }}</span>
            <span class="svp-inspector__nav">
              <v-btn icon x-small :disabled="inspectorIndex === 0" aria-label="Previous row" @click="toggleInspector(inspectorIndex - 1)">
                <v-icon size="16">mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled="inspectorIndex >= sampleRows.length - 1" aria-label="Next row" @click="toggleInspector(inspectorIndex + 1)">
                <v-icon size="16">mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn icon x-small aria-label="Close row details" @click="closeInspector">
                <v-icon size="15">mdi-close</v-icon>
              </v-btn>
            </span>
          </div>
          <div class="svp-inspector__body">
            <div v-for="col in sampleColumns" :key="`i-${col.key}`" class="svp-inspector__field">
              <div class="svp-inspector__k">{{ col.label }}</div>
              <div
                class="svp-inspector__v"
                :class="{ 'svp-cell--mono': inspectorCell(col).mono, 'svp-cell--blank': inspectorCell(col).blank }">{{ inspectorCell(col).text }}</div>
            </div>
          </div>
        </template>
      </aside>
    </div>

    <!-- Count on builder-v1: stage the widget at a realistic default-tile size on a neutral
         backdrop so the KPI preview reads as a card floating on the board, NOT one stretched to
         fill the dialog (the look the user rejected). It renders the SAME <dashboard-widget> with
         the SAME builder-header as the board, so the card is identical by construction — only the
         surrounding frame differs. -->
    <div v-else-if="isKpiStage" class="svp-kpi-stage">
      <div class="svp-kpi-tile">
        <dashboard-widget
          ref="dashboardWidget"
          :message="previewWidget"
          :library-schemas="librarySchemas"
          :builder-header="true"
          :key="previewKey">
        </dashboard-widget>
      </div>
    </div>

    <!-- Preview render (charts, etc.). builder-header opts the widget into the ratified A+ card.
         The document-level A+ rules (.cdb-builder-v1 …, defined unscoped in CustomDashboard.vue)
         reach it via the .cdb-builder-v1 marker on the preview root above, even though the wizard
         dialog is teleported outside the app's builder-v1 root. Flag-off: builder-header false →
         byte-identical legacy render. -->
    <div v-else style="width: 100%; height: 100%">
      <dashboard-widget
        ref="dashboardWidget"
        :message="previewWidget"
        :library-schemas="librarySchemas"
        :builder-header="true"
        :key="previewKey">
      </dashboard-widget>
    </div>
  </div>
</template>

<script>
import DashboardWidget from '@/features/Chatbot/components/dashboard-widget.vue';
import DashboardWidgetSkeleton from './DashboardWidgetSkeleton.vue';
import apiService from '../service/dashboard-widgets.service';
import { log, error } from '@/utils/debugLogger';
import {
  REGISTER_NOT_CONFIGURED_COPY,
  isRegisterConfigurationRequiredError,
} from '@/utils/registerConfigurationError';
import { FRIENDLY_TYPES, friendlyType, isGuidValue, midTruncate, formatDateValue } from '../utils/columnTypes';

/** Preview-only pseudo-type: register key columns are nvarchar, but GUID values get
 *  mono + middle truncation and an "ID" header label so rows stay distinguishable. */
const GUID_TYPE = Object.freeze({ key: 'guid', label: 'ID' });

export default {
  components: {
    DashboardWidget,
    DashboardWidgetSkeleton,
  },

  props: {
    /** SQL view config object from the wizard */
    config: {
      type: Object,
      default: null,
    },
    /** The current tab's coloring schemas (registry), forwarded to dashboard-widget so a reference
     *  schema resolves in the preview. null = loading/none. A pure pass-through — inert until
     *  dashboard-widget declares the prop and threads it into resolveSchema (Step-5 consumer work). */
    librarySchemas: {
      type: Array,
      default: null,
    },
    /** The messageType for visualization (pie, bar, datagrid, table, count, etc.) */
    messageType: {
      type: String,
      default: 'table',
    },
    /** Title shown above the preview */
    title: {
      type: String,
      default: '',
    },
    /** Subtitle shown below title */
    subTitle: {
      type: String,
      default: '',
    },
    /** Font size index (0=small, 1=medium, 2=large) */
    fontSize: {
      type: Number,
      default: 1,
    },
    /** Canonical widget-density level (small|normal|large); forwarded to dashboard-widget
     *  alongside the legacy fontSize above so the live preview matches the saved widget. */
    widgetSize: {
      type: String,
      default: 'normal',
    },
    /** Legend config for chart types */
    legend: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      previewData: null,
      // Safe (category-only) summary of a failed preview request, or null. A set value
      // renders the distinct error state instead of the no-data message, so an API
      // failure never masquerades as a legitimately empty result.
      previewError: null,
      // AVA-602: the source is a Facility Group register with rows but no Shared/Separated
      // mode. Its own calm state, checked before previewError so the generic 400 summary
      // ("not valid for a preview") never shows for it.
      previewConfigRequired: false,
      notConfiguredCopy: REGISTER_NOT_CONFIGURED_COPY,
      previewKey: 0,
      debounceTimer: null,
      // Monotonic request token for the latest-wins guard: every fetch captures the
      // current value, and only a response whose token still matches is applied, so a
      // slower response for an older configuration can't overwrite the current preview.
      fetchSeq: 0,
      // Row inspector: index of the clicked sample row (null = closed)
      inspectorIndex: null,
    };
  },

  computed: {
    // Count/KPI → stage the widget at a realistic tile size on a neutral backdrop instead of
    // stretching the card to fill the dialog. The template's loading / no-data guards run before
    // this branch, so it only renders once there is content to show.
    isKpiStage() {
      return this.messageType === 'count';
    },

    previewWidget() {
      if (!this.previewData) return null;

      return {
        id: 'preview',
        title: this.title || 'Preview',
        subTitle: this.subTitle || '',
        messageType: this.messageType,
        content: this.previewData.content || [],
        series: this.previewData.series || [],
        fontSize: this.fontSize,
        widgetSize: this.widgetSize,
        legend: this.legend,
        sqlViewConfig: this.config,
      };
    },

    hasContent() {
      return this.previewData &&
        Array.isArray(this.previewData.content) &&
        this.previewData.content.length > 0;
    },

    /**
     * Raw-data sample table is used only for tabular message types. Charts,
     * counts, etc. keep delegating to <dashboard-widget>.
     */
    isRawTable() {
      return (this.messageType === 'table' || this.messageType === 'datagrid') && this.hasContent;
    },

    /** Preview raw tables do not render the dashboard datagrid toolbar or title header. */
    isTabularMessageType() {
      return this.messageType === 'table' || this.messageType === 'datagrid';
    },

    /** Sample rows exactly as returned by the preview endpoint. */
    sampleRows() {
      return this.hasContent ? this.previewData.content : [];
    },

    /**
     * Column descriptors ({ key, label, index }) in config.columns order.
     * Row shape matches <dashboard-widget> buildTable(): an array of objects
     * keyed by column name. config.columns is a string[] of those names, so the
     * label is the raw key (display names are not passed to this component).
     * Defensive fallbacks: object-form column entries, array-of-arrays rows,
     * and any row keys not covered by config.columns.
     */
    sampleColumns() {
      const rows = this.sampleRows;
      if (!rows.length) return [];

      const first = rows[0];
      const configCols = Array.isArray(this.config && this.config.columns)
        ? this.config.columns
        : [];

      // Defensive: positional array-of-arrays rows.
      if (Array.isArray(first)) {
        return first.map((_, i) => ({
          key: i,
          index: i,
          label: configCols[i] != null ? String(configCols[i]) : `Column ${i + 1}`,
        })).map(c => this.decorateColumn(c));
      }

      const rowKeys = first && typeof first === 'object' ? Object.keys(first) : [];
      const ordered = [];
      const seen = new Set();

      configCols.forEach(col => {
        const key = typeof col === 'string'
          ? col
          : (col && (col.name || col.value || col.column || col.field));
        if (key == null || seen.has(key) || !rowKeys.includes(key)) return;
        const label = (col && typeof col === 'object')
          ? (col.displayName || col.name || String(key))
          : String(key);
        ordered.push({ key, label, index: rowKeys.indexOf(key) });
        seen.add(key);
      });

      // Safety net: surface any data columns not covered by config.columns.
      rowKeys.forEach((key, i) => {
        if (seen.has(key)) return;
        ordered.push({ key, label: String(key), index: i });
        seen.add(key);
      });

      return (ordered.length
        ? ordered
        : rowKeys.map((key, i) => ({ key, label: String(key), index: i }))
      ).map(c => this.decorateColumn(c));
    },

    /** Per-row formatted cells aligned with sampleColumns. */
    formattedRows() {
      return this.sampleRows.map(row => this.sampleColumns.map(col => this.cellFor(row, col)));
    },

    inspectorRow() {
      if (this.inspectorIndex === null) return null;
      return this.sampleRows[this.inspectorIndex] || null;
    },

    noDataMessage() {
      if (!this.config || !this.config.tableName) return 'Select a data source to see a preview';
      if (!this.config.columns || !this.config.columns.length) return 'Select at least one column';
      return 'No data returned for this configuration';
    },
  },

  watch: {
    // Inputs changed → the open inspector row no longer matches what will render;
    // close it NOW rather than after the debounced fetch lands (previewConfig is a
    // cached computed upstream, so this only fires on real config mutations).
    config: {
      handler() {
        this.inspectorIndex = null;
        this.debouncedFetch();
      },
      deep: true,
      immediate: true,
    },
    messageType() {
      this.inspectorIndex = null;
      this.debouncedFetch();
    },
    // Completion backstop: new data means new rows; a surviving index would show
    // the wrong record.
    previewData() {
      this.inspectorIndex = null;
    },
  },

  methods: {
    debouncedFetch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchPreview();
      }, 500);
    },

    fetchPreview() {
      // Latest-wins guard: bump the request token up front so every call (including the
      // invalid-config early returns below) invalidates any request still in flight. Only
      // a response whose token still equals fetchSeq is applied. Mirrors the
      // sessionRefreshSeq idiom in CustomDashboard.vue (doRefreshFilterSession).
      const seq = ++this.fetchSeq;

      if (!this.config || !this.config.tableName) {
        this.previewData = null;
        this.previewError = null;
        this.previewConfigRequired = false;
        // Clear the spinner: a superseded in-flight response is guarded out of its own
        // finally, so without this the loading skeleton would linger after config clears.
        this.loading = false;
        return;
      }

      if (!this.config.columns || !this.config.columns.length) {
        this.previewData = null;
        this.previewError = null;
        this.previewConfigRequired = false;
        this.loading = false;
        return;
      }

      this.loading = true;
      this.previewError = null;
      this.previewConfigRequired = false;

      const payload = {
        tableName: this.config.tableName,
        columns: this.config.columns,
        aggregation: this.config.aggregation,
        filters: this.config.filters,
        messageType: this.messageType,
        facilityId: this.$route.params.id,
        take: 25,
      };

      apiService.tablePreview(payload)
        .then(res => {
          // Superseded by a newer fetch: drop silently, never touch the current preview.
          if (seq !== this.fetchSeq) {
            log('SqlViewWidgetPreview', 'dropped stale preview response', { seq, latest: this.fetchSeq });
            return;
          }
          this.previewData = res.data;
          this.previewError = null;
          this.previewConfigRequired = false;
          this.previewKey++;
          // Parents harvest column sample values from the fetched rows (column chooser).
          this.$emit('data-loaded', res.data);

          // Verify the sample row shape drives the compact table correctly.
          if ((this.messageType === 'table' || this.messageType === 'datagrid') &&
            res.data && Array.isArray(res.data.content) && res.data.content.length) {
            const firstRow = res.data.content[0];
            log('SqlViewWidgetPreview', 'sample row shape', {
              messageType: this.messageType,
              rowCount: res.data.content.length,
              firstRow,
              rowKeys: firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow)
                ? Object.keys(firstRow)
                : '(non-object row)',
              configColumns: this.config && this.config.columns,
            });
          }
        })
        .catch(err => {
          // Stale failure: a newer fetch already supersedes it, so drop it silently
          // (debug log only) and never surface it as the current error.
          if (seq !== this.fetchSeq) {
            log('SqlViewWidgetPreview', 'dropped stale preview error', { seq, latest: this.fetchSeq });
            return;
          }
          if (isRegisterConfigurationRequiredError(err)) {
            log('SqlViewWidgetPreview', 'source register needs an assignment mode', this.config && this.config.tableName);
            this.previewData = null;
            this.previewError = null;
            this.previewConfigRequired = true;
            return;
          }
          error('SqlViewWidgetPreview', 'fetchPreview error', err);
          // Error is its own state: clear stale rows and surface a category-only summary
          // (the full error stays in the dev log above; the UI never shows raw server text).
          this.previewData = null;
          this.previewError = this.safeErrorSummary(err);
        })
        .finally(() => {
          // A stale response must not clear the spinner while the latest request is still
          // in flight; that newer request owns loading and clears it in its own finally.
          if (seq !== this.fetchSeq) return;
          this.loading = false;
        });
    },

    /** Category-only summary of a preview failure. The full error is logged for devs;
     *  this string is all the UI shows, so a SQL-view failure can never leak schema or
     *  query detail. Copy is kept free of em dashes to match the wizard style. */
    safeErrorSummary(err) {
      const status = err && err.response && err.response.status;
      if (status === 400 || status === 422) return 'This configuration is not valid for a preview.';
      if (status === 401 || status === 403) return 'You do not have access to preview this data.';
      if (status === 404) return 'The selected data source could not be found.';
      if (status >= 500) return 'The server could not process this preview. Try again.';
      if (status) return `The preview request failed (status ${status}).`;
      return 'Could not load the preview. Check your connection and try again.';
    },

    /** Adds display label + friendly type to a column descriptor. Type comes from
     *  config.columnTypes; values refine it (register key columns are nvarchar GUIDs,
     *  and aggregated columns like Count are absent from the map entirely). */
    decorateColumn(entry) {
      const displayNames = (this.config && this.config.columnDisplayNames) || {};
      const types = (this.config && this.config.columnTypes) || {};
      const firstVal = this.firstValueOf(entry);
      let ftype = friendlyType(types[entry.key]);
      if (ftype.key === 'text') {
        if (typeof firstVal === 'string' && isGuidValue(firstVal)) ftype = GUID_TYPE;
        else if (!types[entry.key] && typeof firstVal === 'number') ftype = FRIENDLY_TYPES.number;
        else if (!types[entry.key] && typeof firstVal === 'boolean') ftype = FRIENDLY_TYPES.bool;
      }
      return { ...entry, label: displayNames[entry.key] || entry.label, ftype };
    },

    /** First non-empty value of a column across the sample rows (type sniffing). */
    firstValueOf(entry) {
      for (const row of this.sampleRows) {
        const v = Array.isArray(row) ? row[entry.index] : (row ? row[entry.key] : undefined);
        if (v !== null && v !== undefined && v !== '') return v;
      }
      return undefined;
    },

    rawCell(row, col) {
      return Array.isArray(row) ? row[col.index] : (row ? row[col.key] : undefined);
    },

    /**
     * Format a cell for the grid: (blank) placeholders, Yes/No booleans, humanized
     * dates, middle-truncated mono GUIDs; objects JSON-encoded so nested values
     * never render as [object Object]. Full value goes to the hover :title.
     */
    cellFor(row, col) {
      const raw = this.rawCell(row, col);
      if (raw === null || raw === undefined || raw === '') {
        return { text: '(blank)', title: '', blank: true };
      }
      if (col.ftype.key === 'bool' || typeof raw === 'boolean') {
        const yes = raw === true || raw === 'true' || raw === 1;
        return { text: yes ? 'Yes' : 'No', title: '' };
      }
      if (col.ftype.key === 'date') {
        return { text: formatDateValue(raw), title: String(raw) };
      }
      let s;
      if (typeof raw === 'object') {
        try { s = JSON.stringify(raw); } catch (err) { s = String(raw); }
      } else {
        s = String(raw);
      }
      if (isGuidValue(s)) return { text: midTruncate(s), title: s, mono: true };
      return { text: s, title: s.length > 34 ? s : '' };
    },

    /** Inspector cell: same formatting, but GUIDs and long text stay FULL length. */
    inspectorCell(col) {
      const row = this.inspectorRow;
      const raw = this.rawCell(row, col);
      if (raw === null || raw === undefined || raw === '') {
        return { text: '(blank)', blank: true };
      }
      if (col.ftype.key === 'bool' || typeof raw === 'boolean') {
        return { text: (raw === true || raw === 'true' || raw === 1) ? 'Yes' : 'No' };
      }
      if (col.ftype.key === 'date') return { text: formatDateValue(raw) };
      let s;
      if (typeof raw === 'object') {
        try { s = JSON.stringify(raw); } catch (err) { s = String(raw); }
      } else {
        s = String(raw);
      }
      return { text: s, mono: isGuidValue(s) };
    },

    /** Row click / prev / next; clicking the open row again closes the inspector. */
    toggleInspector(idx) {
      this.inspectorIndex = this.inspectorIndex === idx ? null : idx;
      if (this.inspectorIndex !== null) {
        log('SqlViewWidgetPreview', 'row inspector open', { row: idx + 1, of: this.sampleRows.length });
      }
    },

    closeInspector() {
      this.inspectorIndex = null;
    },

    /** Force a refresh from parent */
    refresh() {
      this.fetchPreview();
    },
  },

  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
};
</script>

<style lang="scss" scoped>
.sql-view-widget-preview {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 200px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 8px;
  overflow: hidden;
}

.theme--dark .sql-view-widget-preview {
  border-color: rgba(255, 255, 255, 0.1);
}

.svp-loading {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
}

/* Error state: distinct from the no-data state. Flat, no side-stripe; severity reads
   from the icon color while the copy stays in the primary text token. */
.svp-error {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  gap: 8px;
  padding: 16px;
  text-align: center;
}

.svp-error__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--v-text-base);
}

.svp-error__detail {
  max-width: 320px;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--v-text-base);
  opacity: 0.7;
}

.svp-error__retry {
  margin-top: 4px;
}

.svp-config-required__icon.v-icon {
  color: var(--v-text-base);
  opacity: 0.45;
}

/* builder-v1: the preview panel reproduces the real dashboard tile card surface
   (DashboardItem's grid-item under .cdb-builder-v1 — CustomDashboard.vue:2545): an elevated
   surface, token border + radius, subtle resting shadow, and the tile's 12px content inset,
   so the KPI preview reads as a real floating tile rather than a flat outlined box. Tokens
   are theme-aware, so one rule serves both themes; placed after the dark border override so
   its token border wins on equal specificity. Flag-off (no .cdb-builder-v1) leaves the panel
   above untouched — byte-identical to the original. */
.sql-view-widget-preview.cdb-builder-v1 {
  background: var(--ds-surface-elevated);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-1);
  padding: 12px;
}

/* Count on builder-v1: the panel becomes a neutral stage (page-tone backdrop, no card chrome
   of its own) that centres ONE realistically-sized tile — so the KPI preview reads as a card on
   the board rather than one stretched to fill the dialog. Overrides the panel-as-card rule above
   (equal specificity, later source wins). Charts/tables are not staged and keep panel-as-card. */
.sql-view-widget-preview.svp-stage-mode {
  background: var(--ds-brand-anti-flash);
  border: none;
  box-shadow: none;
  padding: 0;
}

.theme--dark .sql-view-widget-preview.svp-stage-mode {
  background: rgba(255, 255, 255, 0.03);
}

.svp-kpi-stage {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  padding: 16px;
}

.svp-kpi-tile {
  /* Realistic default count tile. New count widgets default to grid width 5 x height 2 on the
     18-col / 100px-row layout (CustomDashboard gridColNum / gridRowHeight, margin [16,16]) ≈
     340x200px. Reproduces the real .cdb-builder-v1 .vue-grid-item card surface (tokens + 12px
     inset) so the staged card is the board tile. */
  width: 340px;
  height: 188px;
  background: var(--ds-surface-elevated);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-1);
  padding: 12px;
  overflow: hidden;
}

/* Typed wide-data sample table + row inspector ------------------------------- */
.svp-sample {
  position: relative;
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  letter-spacing: 0;
}

.svp-sample__scroll {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  background: var(--ds-surface-elevated, #fcfdfc);
}

.svp-sample__table {
  width: max-content;
  /* separate (not collapse) so each cell owns its border and the sticky header
     keeps its bottom edge while scrolling; border-spacing 0 avoids gaps. */
  border-collapse: separate;
  border-spacing: 0;
  letter-spacing: 0;
}

.svp-sample__table th,
.svp-sample__table td {
  min-width: 120px;
  height: 34px;
  padding: 0 12px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--ds-border-subtle);
  letter-spacing: 0;
  white-space: nowrap;
}

.svp-sample__table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 44px;
  background: var(--ds-surface-elevated, #fcfdfc);
}

.svp-h-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.svp-h-type {
  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.2;
  opacity: 0.55;
  text-transform: uppercase;
}

/* Per-type alignment and widths */
.svp-t-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
  min-width: 84px;
}

.svp-t-guid {
  min-width: 130px;
}

.svp-t-bool {
  min-width: 80px;
}

.svp-t-date {
  font-variant-numeric: tabular-nums;
  min-width: 140px;
}

.svp-row {
  cursor: pointer;
}

.svp-row:hover {
  background: var(--ds-surface-hover);
}

.svp-row:focus-visible {
  outline: 2px solid rgba(3, 98, 76, 0.45);
  outline-offset: -2px;
}

.svp-row--sel,
.svp-row--sel:hover {
  background: #eef5f2;
}

.svp-sample__table tbody td {
  font-weight: 400;
}

/* Ellipsis clamp lives on the inner block, not the cell: max-width on a table
   cell is ignored under table-layout: auto, so the span is what caps width. */
.svp-cell {
  display: block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.svp-cell--mono {
  font-family: ui-monospace, Consolas, 'IBM Plex Mono', monospace;
  font-size: 12px;
  opacity: 0.8;
}

.svp-cell--blank {
  opacity: 0.45;
}

/* Row inspector: full values of the selected sample row */
.svp-inspector {
  flex: 0 0 auto;
  width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--ds-surface-elevated, #fcfdfc);
  transition: width 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.svp-inspector--open {
  width: 300px;
  border-left: 1px solid var(--ds-border-subtle);
}

.svp-inspector__head {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 6px 8px 14px;
  border-bottom: 1px solid var(--ds-border-subtle);
  white-space: nowrap;
  flex: 0 0 auto;
}

.svp-inspector__title {
  font-weight: 600;
  font-size: 13px;
}

.svp-inspector__nav {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2px;
}

.svp-inspector__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 0;
}

.svp-inspector__field {
  padding: 6px 14px;
}

.svp-inspector__k {
  font-size: 10.5px;
  text-transform: uppercase;
  opacity: 0.55;
}

.svp-inspector__v {
  font-size: 13px;
  margin-top: 1px;
  word-break: break-word;
  white-space: normal;
}

.svp-inspector__v.svp-cell--mono {
  font-size: 12px;
}

.theme--dark .svp-row--sel,
.theme--dark .svp-row--sel:hover {
  background: rgba(44, 194, 149, 0.08);
}
</style>
